import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useSearchParams } from 'react-router';
import { CertificateStatus } from 'softcat:components/certificate-status.tsx';
import { ConfirmationModal } from 'softcat:components/confirmation-modal.tsx';
import { DataTable } from 'softcat:components/data-table.tsx';
import { SearchParametersOrderBy } from 'softcat:components/search-parameters-order-by.tsx';
import { SearchParametersPagination } from 'softcat:components/search-parameters-pagination.tsx';
import { SearchParametersSearchField } from 'softcat:components/search-parameters-search-field.tsx';
import { SearchParametersShowLatestAppVersionsOnly } from 'softcat:components/search-parameters-show-latest-app-versions-only.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { WidgetService } from 'softcat:services/widget.ts';
import useSWR, { preload } from 'swr';

export const Apps = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const [searchParameters] = useSearchParams();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [selectedApp, setSelectedApp] = useState({ id: '', name: '', version: '' });
    const [onRemoveErrorMessage, setOnRemoveErrorMessage] = useState('');
    const limit = Number.parseInt(searchParameters.get('limit') || '10');
    const page = Number.parseInt(searchParameters.get('page') || '1');
    const latest = searchParameters.get('showLatestVersionsOnly') !== 'false';
    const query = searchParameters.get('query') || '';
    const orderBy = searchParameters.get('orderBy') || '';

    const { data, error, isLoading, mutate } = useSWR(
        { key: `${activeTerritory}-WidgetServiceFindAll`, latest, limit, orderBy, page: page - 1, query },
        WidgetService.findAll,
    );

    const columns = useMemo(() => {
        return [
            { key: 'id', value: intl.formatMessage({ id: 'APPS_PAGE_COLUMN_ID' }), visible: true },
            { key: 'name', value: intl.formatMessage({ id: 'APPS_PAGE_COLUMN_NAME' }), visible: true },
            { key: 'version', value: intl.formatMessage({ id: 'APPS_PAGE_COLUMN_VERSION' }), visible: true },
            { key: 'size', value: intl.formatMessage({ id: 'APPS_PAGE_COLUMN_SIZE' }), visible: true },
            { key: 'status', value: intl.formatMessage({ id: 'APPS_PAGE_COLUMN_CERTIFICATE_STATUS' }), visible: true },
            { key: 'actions', value: intl.formatMessage({ id: 'APPS_PAGE_COLUMN_ACTIONS' }), visible: false },
        ];
    }, [intl]);

    const removeApp = async () => {
        try {
            await WidgetService.remove({ id: selectedApp.id, version: selectedApp.version });
            await mutate();

            setShowConfirmDelete(false);
        } catch (error) {
            setOnRemoveErrorMessage(`${intl.formatMessage({ id: 'APPS_PAGE_CONFIRM_DELETE_ERROR' })}: ${(error as Error).message}`);
        }
    };

    const rows = useMemo(() => {
        return (
            data?.map((app, index) => {
                return {
                    actions: (
                        <div className={'flex gap-4 justify-self-end'}>
                            <NavLink
                                data-testid={`edit-app-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_EDIT' })}
                                data-tooltip-id={'app-tooltip'}
                                onMouseEnter={() => {
                                    return preload({ id: app.id, key: `${activeTerritory}-WidgetServiceFindOne`, version: app.version }, WidgetService.findOne);
                                }}
                                to={`edit/${app.id}/${app.version}`}
                                type="button"
                            >
                                <PencilIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </NavLink>
                            <button
                                data-testid={`delete-app-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_DELETE' })}
                                data-tooltip-id={'app-tooltip'}
                                onClick={() => {
                                    setSelectedApp({ id: app.id, name: app.name, version: app.version });
                                    setOnRemoveErrorMessage('');
                                    setShowConfirmDelete(true);
                                }}
                            >
                                <TrashIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </button>
                        </div>
                    ),
                    id: app.id,
                    name: app.name,
                    size: `${(app.size / 1024 / 1024).toFixed(2)}MB`,
                    status: <CertificateStatus certificateExpiry={app.publisherNotAfter} />,
                    version: app.version,
                };
            }) || []
        );
    }, [activeTerritory, data, intl]);

    return (
        <div className="space-y-6 px-4">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">
                        <FormattedMessage id={'APPS_PAGE_HEADING'} />
                    </h1>

                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'APPS_PAGE_DESCRIPTION'} />
                    </p>
                </div>

                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    <NavLink
                        className="flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        to={'create'}
                        type="button"
                    >
                        <PlusIcon className={'size-3'} />
                        <FormattedMessage id={'APPS_PAGE_ADD_BUTTON'} />
                    </NavLink>
                </div>
            </div>

            <div className={'flex items-center gap-4'}>
                <div className={'grow'}>
                    <SearchParametersSearchField placeholder={intl.formatMessage({ id: 'APPS_PAGE_SEARCH_PLACEHOLDER' })} />
                </div>

                <div>
                    <SearchParametersOrderBy
                        options={[
                            { label: intl.formatMessage({ id: 'APPS_PAGE_ORDER_BY_NONE' }), value: '' },
                            { label: intl.formatMessage({ id: 'APPS_PAGE_ORDER_BY_ID_DESC' }), value: 'id:desc' },
                            { label: intl.formatMessage({ id: 'APPS_PAGE_ORDER_BY_ID_ASC' }), value: 'id:asc' },
                            { label: intl.formatMessage({ id: 'APPS_PAGE_ORDER_BY_NAME_DESC' }), value: 'name:desc' },
                            { label: intl.formatMessage({ id: 'APPS_PAGE_ORDER_BY_NAME_ASC' }), value: 'name:asc' },
                            { label: intl.formatMessage({ id: 'APPS_PAGE_ORDER_BY_VERSION_DESC' }), value: 'version:desc' },
                            { label: intl.formatMessage({ id: 'APPS_PAGE_ORDER_BY_VERSION_ASC' }), value: 'version:asc' },
                            { label: intl.formatMessage({ id: 'APPS_PAGE_ORDER_BY_SIZE_DESC' }), value: 'size:desc' },
                            { label: intl.formatMessage({ id: 'APPS_PAGE_ORDER_BY_SIZE_ASC' }), value: 'size:asc' },
                        ]}
                    />
                </div>

                <div>
                    <SearchParametersShowLatestAppVersionsOnly />
                </div>
            </div>

            {error && (
                <p className="mt-2 text-sm text-gray-700">
                    <FormattedMessage id={'APPS_PAGE_ERROR_LOADING_DATA'} />
                </p>
            )}

            {isLoading && <Spinner />}

            {!error && !isLoading && (
                <div>
                    {rows.length > 0 ? (
                        <>
                            <div className={'overflow-auto'}>
                                <DataTable columns={columns} rows={rows} />
                            </div>

                            <div className={'border-t border-t-gray-200 pt-2'}>
                                <SearchParametersPagination total={rows.length} />
                            </div>
                        </>
                    ) : (
                        <p className="mt-2 text-sm text-gray-700">
                            <FormattedMessage id={'APPS_PAGE_NO_RESULTS'} />
                        </p>
                    )}
                </div>
            )}

            <ConfirmationModal
                confirmButtonText={intl.formatMessage({ id: 'BUTTON_DELETE' })}
                confirmErrorMessage={onRemoveErrorMessage}
                content={intl.formatMessage({ id: 'APPS_PAGE_CONFIRM_DELETE_DESCRIPTION' }, { app: selectedApp.name })}
                handleClose={() => {
                    setShowConfirmDelete(false);
                }}
                handleConfirm={async () => {
                    await removeApp();
                }}
                header={intl.formatMessage({ id: 'APPS_PAGE_CONFIRM_DELETE_TITLE' })}
                isVisible={showConfirmDelete}
            />
        </div>
    );
};
